import { Badge } from '@up2stay/up2promo-ui';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import {
  ReputationLayout,
  ReputationPageBanners,
} from '@/core/components/reputation/reputation-layout';
import { ReputationPageBreadcrumb } from '@/core/components/reputation/reputation-page-breadcrumb';
import { ReputationSetupAlertsSection } from '@/core/components/reputation/setup/reputation-setup-alerts-section';
import { ReputationSetupAnalysisSection } from '@/core/components/reputation/setup/reputation-setup-analysis-section';
import { ReputationSetupConnectionsSection } from '@/core/components/reputation/setup/reputation-setup-connections-section';
import {
  ReputationSetupNav,
  SETUP_SECTION_IDS,
  type SetupSectionId,
  scrollToSetupSection,
} from '@/core/components/reputation/setup/reputation-setup-nav';
import { ReputationSetupResponsesSection } from '@/core/components/reputation/setup/reputation-setup-responses-section';
import { ReputationSetupSummary } from '@/core/components/reputation/setup/reputation-setup-summary';
import { useSetupAutosaveStatus } from '@/core/components/reputation/setup/setup-save-status';
import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import { SetupSection } from '@/core/components/reputation/setup/setup-section';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useDictionary } from '@/core/providers/dictionary-provider';

function isSetupSection(value: string | null): value is SetupSectionId {
  return SETUP_SECTION_IDS.includes(value as SetupSectionId);
}

export function ReputationSetupPage() {
  const dict = useDictionary();
  const d = dict.reputation.setup;
  // The reply preview needs Reputation AI; only then promise it (DEV-771).
  const { isFeatureEnabled } = useEntitlements();
  const reputationAiEnabled = isFeatureEnabled('feature.reputation_ai');
  const routerLocation = useLocation();
  const params = new URLSearchParams(routerLocation.search);
  const tabParam = params.get('tab');
  const locationId = params.get('locationId') ?? undefined;
  const autosave = useSetupAutosaveStatus();
  const cue = (
    <AutosaveStatusCue
      status={autosave}
      labels={{ saved: d.saveStatus.saved }}
    />
  );

  // Deep links (?tab=responses&locationId=…) land on the matching section.
  useEffect(() => {
    if (!isSetupSection(tabParam)) return;
    const timer = window.setTimeout(() => scrollToSetupSection(tabParam), 250);
    return () => window.clearTimeout(timer);
  }, [tabParam]);

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0 max-w-full">
          <PageHeader
            title={d.title}
            eyebrow={cue}
            actions={
              <AutosaveStatusCue
                status={autosave}
                labels={{ saved: d.saveStatus.saved }}
                className="max-md:hidden"
              />
            }
            breadcrumb={
              <ReputationPageBreadcrumb pageLabel={dict.nav.reputationSetup} />
            }
          />
          <ReputationLayout>
            <ReputationPageBanners hideConnectionBanner />
            <div className="flex min-w-0 flex-col gap-5">
              <ReputationSetupSummary />
              <div className="grid min-w-0 gap-7 xl:grid-cols-[180px_minmax(0,1fr)]">
                <ReputationSetupNav
                  labels={d.tabs}
                  className="sticky top-4 hidden self-start xl:flex"
                />
                <div className="flex min-w-0 flex-col gap-9">
                  <SetupSection
                    id="connections"
                    title={d.sections.connections.title}
                    description={d.sections.connections.description}
                  >
                    <ReputationSetupConnectionsSection />
                  </SetupSection>
                  <SetupSection
                    id="analysis"
                    title={d.sections.analysis.title}
                    description={d.sections.analysis.description}
                  >
                    <ReputationSetupAnalysisSection
                      highlightLocationId={locationId}
                    />
                  </SetupSection>
                  <SetupSection
                    id="responses"
                    title={d.sections.responses.title}
                    description={
                      reputationAiEnabled
                        ? `${d.sections.responses.description} ${d.sections.responses.previewHint}`
                        : d.sections.responses.description
                    }
                  >
                    <ReputationSetupResponsesSection
                      initialLocationId={locationId}
                    />
                  </SetupSection>
                  <SetupSection
                    id="alerts"
                    title={d.sections.alerts.title}
                    description={d.sections.alerts.description}
                    badge={
                      <Badge variant="secondary">
                        {d.summary.soonAvailable}
                      </Badge>
                    }
                  >
                    <ReputationSetupAlertsSection />
                  </SetupSection>
                </div>
              </div>
            </div>
          </ReputationLayout>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
